import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import { Header, Footer } from "../../components/Orchestrio";

const SupportPage = () => {
  return (
    <div className="min-h-screen bg-[#1E1E1E]" data-testid="support-service-page">
      <Header />

      {/* Header */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <Link to="/services" className="text-[#5F8FB8] text-sm hover:underline mb-6 inline-block">
            ← Back to Services
          </Link>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            Ongoing Support
          </h1>
          <p className="text-xl text-[#5F8FB8] mb-4">
            Maintenance, improvements, monitoring
          </p>
          <p className="text-lg text-[#9CA3AF] max-w-2xl leading-relaxed">
            Long-term technical partnership.
          </p>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-6">The problem we solve</h2>
          <div className="max-w-2xl">
            <p className="text-[#9CA3AF] leading-relaxed mb-4">
              Your website or application works—until it doesn't. Security vulnerabilities emerge. 
              Dependencies need updating. Users find edge cases that break things.
            </p>
            <p className="text-[#9CA3AF] leading-relaxed mb-4">
              You don't have a full-time developer, but you need someone who understands your system 
              and can fix problems quickly when they arise.
            </p>
            <p className="text-[#9CA3AF] leading-relaxed">
              You also have a list of improvements you'd like to make—but no reliable way to get them done.
            </p>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">How we approach it</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold mb-2">We know your system</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                We onboard properly, understanding your codebase, infrastructure, and business context. 
                When something breaks, we can fix it fast because we already know how it works.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">We're proactive</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                We don't wait for things to break. We monitor performance, apply security updates, 
                and flag potential issues before they become emergencies.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">We're responsive</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                When something does go wrong, we respond quickly. Critical issues get immediate attention. 
                You won't be waiting days for a reply.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">We help you improve</h3>
              <p className="text-sm text-[#9CA3AF] leading-relaxed">
                Included hours can be used for small improvements and new features. 
                Your system gets better over time, not just maintained.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Support Tiers */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">Support plans</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 bg-[#242424] border border-[#2E2E2E]">
              <h3 className="font-bold mb-2">Essential</h3>
              <div className="mb-4">
                <span className="text-2xl font-bold">£500</span>
                <span className="text-[#6B7280]">/month</span>
              </div>
              <p className="text-sm text-[#5F8FB8] mb-4">Up to 4 hours/month</p>
              <ul className="space-y-3">
                {[
                  "Security updates",
                  "Uptime monitoring",
                  "Monthly backup verification",
                  "Email support (24hr response)",
                  "Small bug fixes"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                    <CheckCircle className="w-4 h-4 text-[#5F8FB8] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-[#6B7280] mt-4 pt-4 border-t border-[#2E2E2E]">
                Best for: Simple websites with low change requirements
              </p>
            </div>

            <div className="p-6 bg-[#242424] border border-[#2F5D8C]">
              <p className="text-[#5F8FB8] text-xs font-medium uppercase tracking-wide mb-2">Most popular</p>
              <h3 className="font-bold mb-2">Standard</h3>
              <div className="mb-4">
                <span className="text-2xl font-bold">£1,200</span>
                <span className="text-[#6B7280]">/month</span>
              </div>
              <p className="text-sm text-[#5F8FB8] mb-4">Up to 10 hours/month</p>
              <ul className="space-y-3">
                {[
                  "Everything in Essential",
                  "Performance monitoring",
                  "Phone support (business hours)",
                  "Content updates",
                  "Minor feature changes",
                  "Quarterly review call"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                    <CheckCircle className="w-4 h-4 text-[#5F8FB8] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-[#6B7280] mt-4 pt-4 border-t border-[#2E2E2E]">
                Best for: Business-critical sites with regular updates
              </p>
            </div>

            <div className="p-6 bg-[#242424] border border-[#2E2E2E]">
              <h3 className="font-bold mb-2">Premium</h3>
              <div className="mb-4">
                <span className="text-2xl font-bold">£3,000</span>
                <span className="text-[#6B7280]">/month</span>
              </div>
              <p className="text-sm text-[#5F8FB8] mb-4">Up to 25 hours/month</p>
              <ul className="space-y-3">
                {[
                  "Everything in Standard",
                  "Priority response (<4hr)",
                  "Feature development",
                  "Performance optimisation",
                  "Infrastructure management",
                  "Monthly review call"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#9CA3AF]">
                    <CheckCircle className="w-4 h-4 text-[#5F8FB8] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-[#6B7280] mt-4 pt-4 border-t border-[#2E2E2E]">
                Best for: Complex applications with ongoing development
              </p>
            </div>
          </div>

          <p className="text-sm text-[#6B7280] mt-6">
            Unused hours don't roll over. Additional hours billed at £120/hour. 
            All plans require 3-month minimum commitment.
          </p>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-6">What's included in all plans</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="font-semibold mb-2">Monitoring</h3>
              <p className="text-sm text-[#9CA3AF]">
                We track uptime, performance, and errors. You'll know if something's wrong before your users do.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Security updates</h3>
              <p className="text-sm text-[#9CA3AF]">
                Framework updates, dependency patches, and vulnerability fixes applied regularly.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Documentation</h3>
              <p className="text-sm text-[#9CA3AF]">
                We maintain runbooks and documentation as we make changes. Your system stays understandable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Best For */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-6">This is right for you if...</h2>
          <ul className="space-y-3 max-w-2xl">
            {[
              "You have a website or application that needs to stay running",
              "You don't have in-house developers (or they're too busy)",
              "You want a reliable partner, not a different freelancer every time",
              "You have ongoing improvement work, not just emergency fixes"
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-[#9CA3AF]">
                <span className="text-[#5F8FB8]">→</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
            <div>
              <h2 className="text-2xl font-bold mb-3">Need reliable support?</h2>
              <p className="text-[#9CA3AF] max-w-lg">
                Get in touch and tell us about your system. 
                We'll recommend the right support level for your needs.
              </p>
            </div>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2 flex-shrink-0">
              Get in touch <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SupportPage;
